import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { DataCatalogModel } from "../model/FileInterface";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import FileUploader from 'devextreme-react/file-uploader';
import { handleError } from "../error/ErrorHandler";
import { LoadPanel } from "devextreme-react/load-panel";

interface Props {
  catalogId?: string;
  hasEditAuth: boolean;
  isAdding: boolean;
  authList: any[];
  cstmList: any[];
}

interface SelectProps {
  value?: string;
  label?: string;
}

interface FileInfo {
  fileId: string;
  fileNm: string;
  sysFileNm?: string;
  fileDir?: string;
}

const ShineEditCatalogContent: FC<Props> = ({ isAdding, catalogId, hasEditAuth, authList, cstmList }) => {
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, formState: { errors } }  = useForm();
  const [ loading, setLoading ] = useState(false);
  const [ fileAuth, setFileAuth ] = useState<SelectProps>({ value: '', label: ''});
  const [ cstm, setCstm ] = useState<SelectProps>({ value: '', label: ''});
  const [ catalog, setCatalog ] = useState<DataCatalogModel>({});
  const [ fileList, setFileList ] = useState<FileInfo[]>([]);

  const save = async (formData: any) => {
    if (!fileList?.length) return alert('파일을 업로드하세요.');

    //저장
    formData['catalogId'] = catalog.catalogId;
    formData['fileAuth'] = fileAuth?.value;
    formData['cstmCd'] = cstm?.value;
    formData['fileId'] = fileList.map(file => file.fileId).join(",");

    setLoading(true);

    try {
      const res = isAdding ? await axios.post('/api/catalog', formData)
                      : await axios.put(`/api/catalog/${formData.catalogId}`, formData);
      if (res.status !== 200) throw new Error();
      return navigate('/catalog');
    } catch(err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => {
    return navigate('/catalog');
  };

  const uploadFile = (event: any) => {
    try {
      const response = JSON.parse(event.request.response);
      setFileList([ ...fileList, response ]);
    } catch(err) {
      handleError(err);
    }
  }

  const removeFile = (file: FileInfo) => {
    const idx = fileList.findIndex( f => f.fileId === file.fileId);
    setFileList([ ...fileList.slice(0, idx), ...fileList.slice(idx + 1)]);
  };

  const getCatalog = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/catalog/${catalogId}`);
      setCatalog(res.data);
      setInitValue(res.data);
      setLoading(false);
    } catch(err) {
      handleError(err);
    }
  }

  const getFileList = async (fileId: string) => {
    try {
      const res = await axios.get(`/api/file/all?fileIdList=${fileId}`);
      setFileList(res.data);
    } catch(err) {
      handleError(err);
    }
  }

  const setInitValue = (catalog: DataCatalogModel) => {
    setValue('catalogDescLrg', catalog.catalogDescLrg || '');
    setValue('catalogDescSml', catalog.catalogDescSml || '');

    const auth = catalog.fileAuth ? authList.filter( auth => auth.value === catalog.fileAuth) [0] : authList[0];
    setFileAuth(auth);

    const cstm = catalog.cstmCd ? cstmList.filter( cstm => cstm.value === catalog.cstmCd) [0] : cstmList[0];
    setCstm(cstm);

    if (catalog.fileId) {
      //TODO 업로드 파일들 불러오기
      getFileList(catalog.fileId);
    }
  }

  useEffect(() => {
    if (!cstmList.length || !authList.length) return;
    if (!isAdding) {
      getCatalog();
    } else {
      setInitValue(catalog);
    }
  }, [ catalogId, cstmList, authList ]);

  return (
    <>
      { loading &&
          <LoadPanel visible={true} />
      }
      <div>
        <div className={'catalog-wrapper'}>
          <div className={'catalog'}>
            <div className='common-form-table-wrapper'>
              <div className='common-form-table'>
                <div className={'common-form-table-header-wrapper'}>
                  <div className={'common-form-table-header-title'}>
                    <span>데이터 카탈로그</span>
                  </div>
                  <div className={'common-form-table-header-alert'}>
                      * 필수사항입니다.
                  </div>
                </div>
                <div className={'common-form-table-body-wrapper'}>
                  <div className={'common-form-table-body'}>

                    <label>
                        <span className={'common-form-input-label'}>
                            데이터 명
                            <span className={'common-form-input-label-alert'}>*</span>
                        </span>
                        <input className={'common-form-input-text lg'} {...register("catalogDescSml", {required: true})} />
                        {errors.fileDescLrg && <span className={'error-msg flex-center'}>필수 입력값입니다.</span>}
                    </label>

                    <label>
                      <span className={'common-form-input-label'}>
                        데이터 설명
                        <span className={'common-form-input-label-alert'}>*</span>
                      </span>
                      <textarea className={'common-form-input-textarea'} placeholder={'500자 이내'} {...register("catalogDescLrg", {required: true})} />
                      {errors.fileDescSml && <span className={'error-msg flex-center'}>필수 입력값입니다.</span>}
                    </label>

                    <label>
                      <span className={'common-form-input-label'}>
                        제공 기관
                        <span className={'common-form-input-label-alert'}>*</span>
                      </span>
                      <Select
                        className="input-border-color width-590"
                        name="cstm"
                        placeholder="참여기관을 선택해주세요."
                        defaultValue={cstmList[0]}
                        value={cstm}
                        options={cstmList}
                        onChange={(newValue) => setCstm(newValue)}
                        menuPortalTarget={document.body} 
                        // styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        isLoading={cstmList.length == 0}
                      />
                    </label>
                    
                    <label>
                      <span className={'common-form-input-label'}>
                        공개 범위
                        <span className={'common-form-input-label-alert'}>*</span>
                      </span>
                      <Select
                        className="input-border-color width-590"
                        name="fileAuth"
                        placeholder="공개범위를 선택해주세요."
                        defaultValue={authList[0]}
                        value={fileAuth}
                        options={authList}
                        onChange={(newValue) => setFileAuth(newValue)}
                        menuPortalTarget={document.body} 
                        // styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        isLoading={authList.length == 0}
                      />
                    </label>
                    <div className="item">
                        <span className={'common-form-input-label'}>
                            첨부파일
                            <span className={'common-form-input-label-alert'}>*</span>
                        </span>
                          <div>
                            { fileList?.map((file: FileInfo, idx: number) => {
                              return (
                                <div key={idx} className={'file-name mt-10'}>
                                  <div>{file.fileNm}</div>
                                  <div>
                                    <button className={"common-button sm alert ml-10"}
                                      onClick={() => removeFile(file)}>삭제</button>
                                  </div>
                                </div>                           
                              )
                          })}
                          <FileUploader selectButtonText="업로드" 
                            name="uploadFile"
                            labelText="" 
                            accept="*"   
                            showFileList={false}
                            uploadMode="instantly"
                            className="input-border-color width-590" 
                            uploadUrl="/api/file/upload"
                            onUploaded={uploadFile} 
                            onUploadError={() => alert('업로드에 실패했습니다')}/>
                          </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      { hasEditAuth && !loading &&
        <div className="flex-center">
          <div>
            <button className={'common-button light'} onClick={cancel}>취소</button>
            <button className={'common-button ml-20 primary'} onClick={handleSubmit(save)}>저장</button>
          </div>
        </div>
      }
    </>
  );
}

export default ShineEditCatalogContent;
